import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { setAppLanguage } from "@/i18n";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    // Auth now rides in an httpOnly cookie, so always ask the server who we are.
    // (A legacy localStorage token, if present, is sent as a Bearer fallback and the
    // server migrates that session to a cookie — after which we purge localStorage.)
    try {
      const res = await api.get("/auth/me");
      setUser(res.data.user);
      if (res.data.user?.language) setAppLanguage(res.data.user.language);
      localStorage.removeItem("jotbay_token");  // migrated to cookie
    } catch (e) {
      localStorage.removeItem("jotbay_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const setSession = (token, userObj) => {
    // Token is set by the server as an httpOnly cookie on login/signup — nothing to store here.
    localStorage.removeItem("jotbay_token");
    sessionStorage.removeItem("jotbay_trial_banner_dismissed");
    sessionStorage.removeItem("jotbay_dunning_dismissed");
    setUser(userObj);
    if (userObj?.language) setAppLanguage(userObj.language);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* clear locally regardless */ }
    localStorage.removeItem("jotbay_token");
    setUser(null);
    window.location.href = "/login";
  };

  const trialDaysLeft = () => {
    return user?.trial_days_left ?? null;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, setSession, logout, refresh: loadMe, trialDaysLeft }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
